import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantsService from '@services/ParticipantsService';
import PolicyModalView from './PolicyModalView';
import { IUser } from '@interfaces/user';
import { usePolicyState } from '@core/myta/components/PolicyModal/context';
import { AnalyticsType } from '@interfaces/analytics';
import { toPollyLanguageCode } from '@constants/language';
import { PollyLanguage } from '@shared/interfaces/polly';

const PolicyModal = () => {
  const { user, setUser, logoutUser, trackAnalytics, appSettings } = useMytaverse();
  const { i18n } = useTranslation();
  const blackListDomains = [
    'zhacity.mytaverse.com',
    'metrotopia.mytaverse.com',
  ];
  const [isEnd, setIsEnd] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { open, viewOnly, setOpen } = usePolicyState();

  useEffect(() => {
    // The auto-open acceptance flow must not override a read-only open
    // triggered manually (e.g. from the login page legal links).
    if (viewOnly) {
      return;
    }

    const isNotInBlackList = blackListDomains.some(
      (domain) => domain === window.location.hostname,
    );

    if (user && !user.isTermsAccepted && !isNotInBlackList) {
      setOpen(true);
      trackAnalytics(AnalyticsType.ViewPrivacyPolicy, { ref: 'welcome' });
    } else {
      setOpen(false);
    }
  }, [user, viewOnly]);

  const handleClose = useCallback(() => {
    // In read-only mode there is no user to log out — just close the dialog.
    if (viewOnly) {
      setOpen(false);
      return;
    }

    if (!user?.isTermsAccepted) {
      logoutUser();
    } else {
      setOpen(false);
    }
  }, [logoutUser, user?.isTermsAccepted, viewOnly, setOpen]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;

    const buffer = 30;

    if (
      target.scrollHeight - target.scrollTop <=
      target.clientHeight + buffer
    ) {
      setIsEnd(true);
    }
  }, []);

  const handleAccept = useCallback(async () => {
    if (!isEnd || !user) {
      return;
    }

    try {
      setIsProcessing(true);
      await ParticipantsService.updateCurrentParticipantProfileTC({
        isTermsAccepted: true,
      });
      trackAnalytics(AnalyticsType.AcceptTerms);

      setUser((prevState: IUser) => ({
        ...prevState,
        isTermsAccepted: true,
      }));

      setOpen(false);
    } catch (e) {
      console.error('Error updating profile:', e);
    } finally {
      setIsProcessing(false);
    }
  }, [user, isEnd, setOpen, trackAnalytics]);

  const termsAndConditions = appSettings?.termsAndConditions;

  // Pick the markdown for the participant's language, falling back to the base
  // language when there is no translation for it.
  const markdown = (() => {
    const content = termsAndConditions?.content?.trim();
    if (!content) {
      return undefined;
    }

    const langKey = toPollyLanguageCode(i18n.language) as PollyLanguage;
    if (langKey === termsAndConditions?.baseLanguage) {
      return termsAndConditions?.content;
    }

    return termsAndConditions?.translations?.[langKey] ?? termsAndConditions?.content;
  })();

  if (!open || (!markdown && !appSettings?.termsPDFUrl)) {
    return null;
  }

  return (
    <PolicyModalView
      file={markdown ? undefined : appSettings?.termsPDFUrl}
      markdown={markdown}
      open={open}
      // Read-only view is not gated by scroll, so it always counts as read.
      isRead={viewOnly || isEnd}
      disabled={!isEnd || isProcessing}
      handleScroll={handleScroll}
      onReachEnd={() => setIsEnd(true)}
      onClose={handleClose}
      onClick={handleAccept}
      isAccepted={!!user?.isTermsAccepted}
      readOnly={viewOnly}
    />
  );
};

export default PolicyModal;
